import { action, flow, makeAutoObservable, observable } from 'mobx';
import type ExplorerRootStore from './ExplorerRootStore';
import { extractErrorMessage } from './utils';
import { isFlowCancellationError } from 'mobx';
import { CancellablePromise } from 'mobx/dist/internal';
import EVMBase from './Connectors/Wallets/EVMBase';
import SolanaWallet from './Connectors/Wallets/SolanaWallet';
import SuiWallet from './Connectors/Wallets/SuiWallet';

type TModalType = 'origin' | 'destination' | 'anyEvm';

export default class ConnectModalStore {
	private readonly root: ExplorerRootStore;

	accountModal = false;

	private walletModal: boolean = false;
	mode: TModalType = 'origin';
	evmConnecting: EVMBase | undefined = undefined;
	evmError: string | undefined = undefined;
	evmRetry: boolean = false;

	solanaWallet: SolanaWallet | undefined = undefined;
	solanaConencting: CancellablePromise<void> | undefined = undefined;
	solanaError: string | undefined = undefined;
	solanaRetry: boolean = false;

	suiWallet: SuiWallet | undefined = undefined;
	suiConnecting: CancellablePromise<void> | undefined = undefined;
	suiError: string | undefined = undefined;
	suiRetry: boolean = false;

	ignoreConnected: boolean = false;

	constructor(root: ExplorerRootStore) {
		this.root = root;
		makeAutoObservable<ConnectModalStore, 'root' | 'walletModal'>(
			this,
			{
				root: false,
				solanaWallet: observable.ref,
				solanaConencting: observable.ref,
				evmConnecting: observable.ref,
				walletModal: observable,
			},
			{ autoBind: true },
		);
	}

	get currentChain(): Readonly<Chain> {
		const chains = this.root.chains;
		return this.mode === 'origin' ? chains.originChain : chains.destinationChain;
	}

	get changable() {
		return true;
	}

	get isOpen() {
		return this.walletModal;
	}

	get isEVM() {
		return this.mode === 'anyEvm' || this.currentChain?.mode === 'EVM';
	}

	get showEvm() {
		return this.isOpen && this.isEVM;
	}

	get showSolana() {
		return this.isOpen && !this.isEVM && this.currentChain?.mode === 'SOLANA';
	}

	get showSui() {
		return this.isOpen && !this.isEVM && this.currentChain?.mode === 'SUI';
	}

	get isDestination() {
		return this.mode === 'destination';
	}

	close() {
		this.walletModal = false;
	}

	open(mode: TModalType, opts: { ignoreConnected?: boolean } = {}) {
		const { ignoreConnected = false } = opts;
		this.ignoreConnected = ignoreConnected;
		this.mode = mode;
		this.walletModal = true;
	}

	openAccount() {
		this.accountModal = true;
	}

	closeAccount() {
		this.accountModal = false;
	}

	selectEvm = flow(function* (this: ConnectModalStore, wallet: EVMBase) {
		if (this.evmConnecting === wallet) {
			return;
		}
		this.evmError = undefined;
		this.evmRetry = true;

		this.evmConnecting = wallet;
		try {
			yield this.root.wallets.activate(wallet, this.mode);
			this.close();
		} catch (e) {
			this.evmError = extractErrorMessage(e);
			if (e && typeof e === 'object' && 'code' in e) {
				switch (e.code) {
					case -32002:
						this.evmError = 'Request already pending! Try resolving the issue in the extension.';
						this.evmRetry = false;
						break;
				}
			}
		}
		this.evmConnecting = undefined;
	});

	selectSui = flow(function* (this: ConnectModalStore, wallet: SuiWallet) {
		if (this.suiWallet === wallet) {
			return;
		}
		this.suiWallet = wallet;
		if (this.suiConnecting) {
			this.suiConnecting.cancel();
		}
		this.suiRetry = true;
		this.suiError = undefined;
		try {
			this.suiConnecting = this.root.wallets.activate(this.suiWallet, this.mode);
			yield this.suiConnecting;
			this.close();
			this.suiWallet = undefined;
		} catch (e) {
			if (isFlowCancellationError(e as Error)) {
				this.suiRetry = false;
			} else {
				console.error(e);
				this.suiError = extractErrorMessage(e) || 'Something went wrong!';
			}
		}
		this.suiWallet = undefined;
		this.suiConnecting = undefined;
	});

	selectSolana = flow(function* (this: ConnectModalStore, wallet: SolanaWallet, activate = true) {
		if (this.solanaWallet === wallet) {
			return;
		}
		this.solanaWallet = wallet;
		if (this.solanaConencting) {
			this.solanaConencting.cancel();
		}
		this.solanaRetry = true;
		this.solanaError = undefined;
		try {
			if (activate) {
				this.solanaConencting = this.root.wallets.activate(wallet, this.mode);
			} else {
				this.solanaConencting = this.root.wallets.svm.connect(wallet);
			}
			yield this.solanaConencting;
			this.close();
			this.solanaWallet = undefined;
		} catch (e) {
			if (isFlowCancellationError(e as Error)) {
				this.solanaRetry = false;
			} else {
				console.error(e);
				this.solanaError = extractErrorMessage(e) || 'Something went wrong!';
			}
		}
		this.solanaWallet = undefined;
		this.solanaConencting = undefined;
	});

	cancelSolana = action(() => {
		this.solanaConencting?.cancel();
		this.solanaWallet = undefined;
	});
}
